import React, { useState } from 'react';
import { useAuthContext } from "../auth/LauncherAuthContext";
import { LauncherLayout } from "../layout/LauncherLayout";
import { EnsureLoggedIn } from "../../admin/EnsureLoggedIn";
import {
    Card, Button, Row, Col, Image, Typography, Modal, Form, Input, Select,
    message, Spin, Flex, Divider, Tag, Tooltip, Space
} from "antd";
import { EditOutlined, PlusOutlined, BookOutlined, SafetyCertificateOutlined, CalendarOutlined, CheckCircleOutlined, ExclamationCircleOutlined, WarningOutlined, TeamOutlined } from "@ant-design/icons";
import { launcherCard } from "../common/styles";
import { useQuery } from "react-query";
import axios, { AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { GeneralImageChooser } from "../../commons/GeneralImageChooser";
import { AdventureDto, InvitationDto } from "../../commons/data/types";
import { GameDto } from "../../games/types";
import { InvitationManagementModal } from "./InvitationManagementModal";
import { TriggerWarningSelector } from "../common/TriggerWarningSelector";
import dayjs from "dayjs";

const { Title, Paragraph, Text } = Typography;

export const OrganizerAdventures: React.FC = () => {
    return (
        <LauncherLayout>
            <EnsureLoggedIn shouldBeOrganizer={true}>
                <OrganizerAdventuresContent />
            </EnsureLoggedIn>
        </LauncherLayout>
    );
};

const OrganizerAdventuresContent: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuthContext();
    const organizer = user?.organizer;

    const [modalOpen, setModalOpen] = useState(false);
    const [editingAdventure, setEditingAdventure] = useState<AdventureDto | null>(null);
    const [form] = Form.useForm();

    // State for Invitation Management Modal
    const [invitationModalOpen, setInvitationModalOpen] = useState(false);
    const [selectedAdventureForInvitation, setSelectedAdventureForInvitation] = useState<AdventureDto | null>(null);
    const [selectedInvitation, setSelectedInvitation] = useState<InvitationDto | null>(null);

    const { data: adventures, refetch, isLoading: listLoading } = useQuery<AdventureDto[]>(
        'organizer-adventures',
        () => axios.get('/api/organizer/adventures').then(res => res.data),
        { enabled: !!organizer }
    );

    const { data: ttrpgGames, isLoading: gamesLoading } = useQuery<GameDto[]>(
        'ttrpg-games',
        () => axios.get<object, AxiosResponse<GameDto[]>>('/api/launcher/games')
            .then(r => (r.data || []).filter(game => game.type?.tag === 'ttrpgs'))
    );

    if (!organizer) {
        return null;
    }

    const handleAdd = () => {
        setEditingAdventure(null);
        form.resetFields();
        setModalOpen(true);
    };

    const handleEdit = (adv: AdventureDto) => {
        setEditingAdventure(adv);
        form.setFieldsValue({
            title: adv.title,
            description: adv.description,
            systemId: adv.systemId,
            coverId: adv.coverId,
            contentWarningIds: adv.contentWarnings?.map(cw => cw.id) || []
        });
        setModalOpen(true);
    };

    const handleSubmit = async (values: any) => {
        try {
            const payload: AdventureDto = {
                title: values.title.trim(),
                description: values.description.trim(),
                systemId: values.systemId,
                coverId: values.coverId,
                contentWarnings: (values.contentWarningIds || []).map((id: string) => ({ id, key: '' }))
            };

            if (editingAdventure?.id) {
                await axios.put(`/api/organizer/adventures/${editingAdventure.id}`, payload);
                message.success(t('organizer.adventures.success.update'));
            } else {
                await axios.post('/api/organizer/adventures', payload);
                message.success(t('organizer.adventures.success.create'));
            }
            setModalOpen(false);
            refetch();
        } catch (err: any) {
            message.error(err.response?.data || t('organizer.adventures.error.save'));
        }
    };

    const openCreateInvitation = (adv: AdventureDto) => {
        setSelectedAdventureForInvitation(adv);
        setSelectedInvitation(null);
        setInvitationModalOpen(true);
    };

    const openManageInvitation = (adv: AdventureDto, inv: InvitationDto) => {
        setSelectedAdventureForInvitation(adv);
        setSelectedInvitation(inv);
        setInvitationModalOpen(true);
    };

    return (
        <>
            <div style={{ maxWidth: '1200px', margin: '40px auto', padding: '0 20px' }}>
                <Card style={{ ...launcherCard, marginBottom: '24px' }}>
                    <Title level={4} style={{ margin: 0 }}>{t('organizer.adventures.title')}</Title>
                    <Divider />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="middle"
                        onClick={handleAdd}
                        style={{ marginBottom: '16px', borderRadius: '8px', width: '100%' }}
                    >
                        {t('organizer.adventures.new')}
                    </Button>

                    {listLoading ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <Row gutter={[24, 24]}>
                            {adventures && adventures.length > 0 ? (
                                adventures.map((adv) => (
                                    <Col xs={24} sm={12} md={8} key={adv.id}>
                                        <AdventureCardItem
                                            adv={adv}
                                            onEdit={() => handleEdit(adv)}
                                            onCreateInvitation={() => openCreateInvitation(adv)}
                                            onManageInvitation={(inv) => openManageInvitation(adv, inv)}
                                        />
                                    </Col>
                                ))
                            ) : (
                                <Col span={24}>
                                    <Card style={{ ...launcherCard, textAlign: 'center', padding: '40px 0' }}>
                                        <BookOutlined style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }} />
                                        <Title level={4}>{t('organizer.adventures.none')}</Title>
                                        <Paragraph type="secondary" style={{ marginBottom: '24px' }}>
                                            {t('organizer.adventures.none.desc')}
                                        </Paragraph>
                                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                                            {t('organizer.adventures.modal.create')}
                                        </Button>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    )}
                </Card>
            </div>

            {/* ADVENTURE EDIT MODAL */}
            <Modal
                title={editingAdventure ? t('organizer.adventures.modal.edit') : t('organizer.adventures.modal.create')}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                okText={t('common.save')}
                cancelText={t('common.cancel')}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    style={{ marginTop: '16px' }}
                >
                    <Form.Item
                        name="title"
                        label={t('organizer.adventures.form.title')}
                        rules={[
                            { required: true, message: t('organizer.adventures.form.title.required') },
                            { max: 50, message: t('organizer.adventures.form.title.maxLength') }
                        ]}
                    >
                        <Input placeholder={t('organizer.adventures.form.title.placeholder')} maxLength={50} showCount />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={t('organizer.adventures.form.description')}
                        rules={[
                            { required: true, message: t('organizer.adventures.form.description.required') },
                            { max: 500, message: t('organizer.adventures.form.description.maxLength') }
                        ]}
                    >
                        <Input.TextArea placeholder={t('organizer.adventures.form.description.placeholder')} rows={4} maxLength={500} showCount />
                    </Form.Item>

                    <Form.Item
                        name="systemId"
                        label={t('organizer.adventures.form.system')}
                        rules={[{ required: true, message: t('organizer.adventures.form.system.required') }]}
                    >
                        <Select
                            placeholder={t('organizer.adventures.form.system.placeholder')}
                            loading={gamesLoading}
                            showSearch
                            filterOption={(input, option) =>
                                ((option?.label) as string ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {ttrpgGames?.map(game => (
                                <Select.Option key={game.id} value={game.id} label={game.title}>
                                    <Flex align="center" justify="space-between">
                                        <Flex justify="center" vertical={true}>
                                            <Image src={game.mediaUrl} preview={false} style={{
                                                marginRight: 10, marginBottom: '10px', borderRadius: 4, objectFit: 'cover'
                                            }} width={24} height={24} />
                                        </Flex>
                                        <div>{game.title}</div>
                                    </Flex>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="contentWarningIds"
                        label={t('organizer.adventures.form.contentWarnings')}
                    >
                        <TriggerWarningSelector />
                    </Form.Item>

                    <Form.Item
                        name="coverId"
                        label={t('organizer.adventures.form.cover')}
                        rules={[{ required: true, message: t('organizer.adventures.form.cover.required') }]}
                    >
                        <GeneralImageChooser />
                    </Form.Item>
                </Form>
            </Modal>

            {/* INVITATION MANAGEMENT MODAL */}
            <InvitationManagementModal
                open={invitationModalOpen}
                adventure={selectedAdventureForInvitation}
                invitation={selectedInvitation}
                onClose={() => setInvitationModalOpen(false)}
                onSuccess={() => {
                    refetch();
                }}
            />
        </>
    );
};

interface AdventureCardItemProps {
    adv: AdventureDto;
    onEdit: () => void;
    onCreateInvitation: () => void;
    onManageInvitation: (inv: InvitationDto) => void;
}

const AdventureCardItem: React.FC<AdventureCardItemProps> = ({
    adv,
    onEdit,
    onCreateInvitation,
    onManageInvitation
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { data: invitations = [], isLoading } = useQuery<InvitationDto[]>(
        ['adventure-invitations', adv.id],
        () => axios.get(`/api/organizer/adventures/${adv.id}/invitations`).then(res => res.data),
        { enabled: !!adv.id }
    );

    const activeInvitation = invitations.find(inv => {
        const deadline = inv.invitationDeadline;
        return deadline > Date.now();
    });

    const handleCardClick = () => {
        if (activeInvitation?.id) {
            navigate(`/invitations/${activeInvitation.id}`);
        }
    };

    return (
        <Card
            hoverable
            onClick={handleCardClick}
            style={{
                ...launcherCard,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                cursor: activeInvitation ? 'pointer' : 'default'
            }}
            cover={
                adv.coverUrl ? (
                    <div style={{ height: '200px', overflow: 'hidden', position: 'relative' }}>
                        <img
                            alt={adv.title}
                            src={adv.coverUrl}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    </div>
                ) : (
                    <div style={{
                        height: '200px',
                        background: '#f5f5f5',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <BookOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                    </div>
                )
            }
            actions={[
                <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                    }}
                    key="edit"
                >
                    {t('common.edit')}
                </Button>
            ]}
        >
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Title level={4} style={{ marginTop: 0, marginBottom: '8px' }} ellipsis={{ rows: 1 }}>
                    {adv.title}
                </Title>
                <div style={{ marginBottom: '8px' }}>
                    <Text type="secondary" strong>{t('organizer.adventures.system')}: </Text>
                    <Text type="secondary">{adv.systemTitle || t('organizer.adventures.unknown')}</Text>
                </div>
                <Paragraph
                    type="secondary"
                    ellipsis={{ rows: 2 }}
                    style={{ marginBottom: '16px' }}
                >
                    {adv.description}
                </Paragraph>

                {adv.contentWarnings && adv.contentWarnings.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                        <Space wrap size={[0, 8]}>
                            {adv.contentWarnings.map(cw => (
                                <Tag key={cw.id} color="orange" icon={<WarningOutlined />}>
                                    {t(cw.key || cw.id)}
                                </Tag>
                            ))}
                        </Space>
                    </div>
                )}

                <Divider style={{ margin: '12px 0' }} />

                {/* INVITATIONS STATUS & ACTION SECTION */}
                <div style={{ marginTop: 'auto' }}>
                    {isLoading ? (
                        <Spin size="small" />
                    ) : activeInvitation ? (
                        <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', padding: '12px', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <Space>
                                    <SafetyCertificateOutlined style={{ color: '#52c41a' }} />
                                    <Text strong style={{ fontSize: '13px' }}>{t('organizer.invitations.active')}</Text>
                                </Space>
                                {activeInvitation.published ? (
                                    <Tag color="green" icon={<CheckCircleOutlined />}>{t('organizer.invitations.published')}</Tag>
                                ) : (
                                    <Tag color="gold" icon={<ExclamationCircleOutlined />}>{t('organizer.invitations.unpublished')}</Tag>
                                )}
                            </div>

                            <div style={{ fontSize: '12px', color: '#555', marginBottom: '10px' }}>
                                <div><CalendarOutlined /> Deadline: {dayjs(activeInvitation.invitationDeadline).format('YYYY-MM-DD HH:mm')}</div>
                                <div>Sessions: {activeInvitation.sessions?.length || 0} / 4</div>
                            </div>

                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button
                                    type="primary"
                                    size="small"
                                    block
                                    icon={<SafetyCertificateOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onManageInvitation(activeInvitation);
                                    }}
                                >
                                    {t('organizer.invitations.title.manage')}
                                </Button>

                                <Tooltip title={t('organizer.invitations.activeExistsTooltip')}>
                                    <span>
                                        <Button
                                            type="dashed"
                                            size="small"
                                            block
                                            disabled
                                            icon={<PlusOutlined />}
                                        >
                                            {t('organizer.invitations.activeExistsButton')}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Space>
                        </div>
                    ) : (
                        <div style={{ background: '#f5f5f5', border: '1px dashed #d9d9d9', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: '8px', fontSize: '13px' }}>
                                {t('organizer.invitations.noActive')}
                            </Text>
                            <Button
                                type="primary"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCreateInvitation();
                                }}
                            >
                                {t('organizer.invitations.title.create')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};
